import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';
import { safeStorage } from '../utils/storage';

interface User {
  id: number;
  name: string;
  email: string;
  whatsapp?: string;
  verified?: boolean;
  email_verified?: boolean;
  preferred_language?: string;
  role?: string;
  created_at?: string;
  updated_at?: string;
}

interface UserAuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: { email: string; password: string }) => Promise<{
    message: string;
    user: any;
    token: string;
  }>;
  register: (userData: {
    name: string;
    email: string;
    whatsapp: string;
    password: string;
    language?: string;
  }) => Promise<{ requires_verification?: boolean }>;
  logout: () => void;
  verifyEmail: (email: string, otp: string) => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
}

const UserAuthContext = createContext<UserAuthContextType | undefined>(undefined);

interface UserAuthProviderProps {
  children: ReactNode;
}

export const UserAuthProvider: React.FC<UserAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize from storage on mount
  useEffect(() => {
    const initializeAuth = () => {
      const token = safeStorage.getItem('authToken');
      const userStr = safeStorage.getItem('user');

      if (token) {
        setAuthToken(token);
        if (userStr) {
          try {
            const userData = JSON.parse(userStr);
            setUser(userData);
          } catch (error) {
            // Error parsing user data
            safeStorage.removeItem('authToken');
            safeStorage.removeItem('user');
            setAuthToken(null);
            setUser(null);
          }
        }
      } else {
        // Ensure state is clean if no token
        setAuthToken(null);
        setUser(null);
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // Listen for storage changes to sync state across tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authToken') {
        const newToken = e.newValue;
        setAuthToken(newToken);

        // If token is removed externally, clear user state
        if (!newToken) {
          setUser(null);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = async (credentials: { email: string; password: string }) => {
    setIsLoading(true);
    try {
      const response = await apiService.login(credentials);
      if (response.user && response.token) {
        setUser(response.user);
        setAuthToken(response.token);
        return response;
      }
      throw new Error('Invalid response from server');
    } catch (error) {
      // Re-throw the error for the login component to handle
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: {
    name: string;
    email: string;
    whatsapp: string;
    password: string;
    language?: string;
  }) => {
    setIsLoading(true);
    try {
      const response = await apiService.register(userData);

      if (!response.requires_verification && response.user && response.token) {
        setUser(response.user);
        setAuthToken(response.token);
      }

      return { requires_verification: response.requires_verification };
    } finally {
      setIsLoading(false);
    }
  };

  const verifyEmail = async (email: string, otp: string) => {
    setIsLoading(true);
    try {
      const response = await apiService.verifyEmail(email, otp);
      if (response.user && response.token) {
        setUser(response.user);
        setAuthToken(response.token);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    apiService.logout();
    setUser(null);
    setAuthToken(null);
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      safeStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  // Derive isAuthenticated from state only, avoiding render-time storage reads
  const isAuthenticated = !!user && !!authToken;

  const value: UserAuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    verifyEmail,
    updateUser,
  };

  return (
    <UserAuthContext.Provider value={value}>
      {children}
    </UserAuthContext.Provider>
  );
};

export const useUserAuth = (): UserAuthContextType => {
  const context = useContext(UserAuthContext);
  if (context === undefined) {
    throw new Error('useUserAuth must be used within a UserAuthProvider');
  }
  return context;
};